import type { ru } from "./strings.ru";

/**
 * Romanian UI dictionary. Typed as `Record<keyof typeof ru, …>` so TypeScript
 * flags any missing keys — if you add something to `ru`, the compiler won't
 * let you commit until `ro` has a matching entry.
 *
 * Plural buckets match `Intl.PluralRules("ro").select(n)` output: one/few/other.
 *
 * Tone: politicos (formă de politețe), concis.
 */
export const ro: Record<keyof typeof ru, string> = {
  /* ─── Bottom nav ─────────────────────────── */
  "nav.home": "Acasă",
  "nav.study": "Învață",
  "nav.vocab": "Cuvinte",
  "nav.grammar": "Gramatică",
  "nav.progress": "Progres",
  "nav.admin": "Admin",
  "nav.levels": "Niveluri",
  "nav.settings": "Setări",

  /* ─── Common ─────────────────────────────── */
  "common.loading": "Se încarcă…",
  "common.failed_to_load": "Eroare la încărcare",
  "common.cancel": "Anulează",
  "common.confirm": "Confirmă",
  "common.back": "Înapoi",
  "common.save": "Salvează",
  "common.saving": "Se salvează…",
  "common.all": "Toate",
  "common.new": "Noi",
  "common.learning": "Se învață",
  "common.known": "Știute",
  "common.min": "min",
  "common.yes": "Da",
  "common.loading_more": "Se încarcă…",

  /* ─── Home ───────────────────────────────── */
  "home.greeting": "Bună!",
  "home.greeting_with_name": "Bună, {name}",
  "home.due_today": "Pentru azi",
  "home.cards": "cartonașe",
  "home.about_minutes": "~{n} min",
  "home.all_caught_up": "Totul e la zi",
  "home.start_session": "Începe sesiunea",
  "home.new_count": "{n} noi",
  "home.reviews_count": "{n} de repetat",
  "home.stat_known": "Știute",
  "home.stat_learning": "Se învață",
  "home.stat_studied": "Studiate",
  "home.study_words": "Cuvinte · {n}",
  "home.study_grammar": "Gramatică · {n}",
  "home.grammar_title": "Gramatică",
  "home.see_all": "Vezi toate",
  "home.grammar_new": "nou",
  "home.grammar_review": "repetă",
  "home.grammar_known": "știut",
  "home.vocabulary": "Vocabular",
  "home.browse_words": "Răsfoiește {n} cuvinte românești",
  "home.streak_aria": "Serie: {n} zile",
  "home.mode_open_label": "Open",
  "home.mode_open_sub": "Liber",
  "home.mode_level_sub": "Nivel",
  "home.mode_hint_open_title": "Modul deschis",
  "home.mode_hint_open_body": "Toate nivelurile amestecate. Treci la „Nivel” pentru un parcurs ghidat.",
  "home.mode_hint_level_title": "Modul nivel",
  "home.mode_hint_level_body": "Doar conținut {level}. Progresul e comun cu modul deschis.",
  "home.mode_hint_switch_link": "Schimbă în Setări",
  "home.path": "Parcurs",
  "home.cards_to_level_up": "{n} cartonașe până la următorul nivel",
  "home.ready_to_advance": "Gata pentru nivelul următor",
  "home.ready_for_exam": "Gata pentru examen!",
  "home.bar_vocab": "VOCABULAR",
  "home.bar_grammar": "GRAMATICĂ",
  "home.cta_continue": "Continuă {level}",
  "home.cta_continue_with_new": "Continuă {level} · {n} noi astăzi",
  "home.cta_final_exam": "Examen final · {level}",
  "home.today_in_level": "Astăzi în {level}",
  "home.row_new_vocab": "Vocabular nou",
  "home.row_reviews": "Repetări",
  "home.row_grammar": "Gramatică",

  /* ─── Words list ─────────────────────────── */
  "words.title": "Cuvinte",
  "words.add": "Adaugă",
  "words.totals": "{total} în total · {known} știute · {learning} se învață",
  "words.search_placeholder": "Caută cuvinte, traduceri…",
  "words.section_all": "Toate cuvintele",
  "words.showing_of": "afișate {shown} din {total}",
  "words.empty_no_match": 'Niciun cuvânt nu se potrivește cu „{q}"',
  "words.empty_no_match_filtered": 'Niciun cuvânt nu se potrivește cu „{q}" în categoria „{filter}".',
  "words.empty_filter": "Încă niciun cuvânt în această categorie.",
  "words.empty_all": "Încă niciun cuvânt. Importați un JSON sau rulați scriptul de seed.",
  "words.filter_by_tag": "Filtru după etichetă:",
  "words.clear_filter": "Șterge",

  /* ─── Word detail ────────────────────────── */
  "word_detail.not_found": "Cuvântul nu a fost găsit",
  "word_detail.back_aria": "Înapoi la cuvinte",
  "word_detail.translation": "Traducere",
  "word_detail.translations": "Traduceri",
  "word_detail.also_prefix": "de asemenea:",
  "word_detail.forms": "Forme",
  "word_detail.forms_indefinite": "nearticulat",
  "word_detail.forms_articulated": "articulat",
  "word_detail.forms_singular": "singular",
  "word_detail.forms_plural": "plural",
  "word_detail.your_progress": "Progresul dvs.",
  "word_detail.examples": "Exemple",
  "word_detail.synonyms": "Sinonime",
  "word_detail.tags": "Etichete",
  "word_detail.edit": "Editează",
  "word_detail.mark_known_new": "Știu acest cuvânt",
  "word_detail.mark_known_learning": "Știu — marchează ca învățat",
  "word_detail.mark_known_confirm":
    'Marcați „{lemma}" ca învățat? Cuvântul va reapărea peste ~4 luni pentru verificare.',
  "word_detail.delete_confirm": "Ștergeți acest cuvânt și toate relațiile?",
  "word_detail.delete_yes": "Da, șterge",
  "word_detail.deleting": "Se șterge…",
  "word_detail.delete_action": "Șterge cuvântul",
  "word_detail.no_image": "fără imagine",
  "word_detail.next_review_overdue": "în întârziere cu {n} zile",
  "word_detail.next_review_today": "pentru azi",
  "word_detail.next_review_days": "peste {n} zile",
  "word_detail.next_review_months": "peste {n} luni",
  "word_detail.related_grammar": "Gramatică relevantă",
  "word_detail.related_grammar_show_more": "Arată încă {n}",
  "word_detail.related_grammar_show_less": "Restrânge",
  "word_detail.matched_by_matchers": "după tipar",
  "word_detail.matched_by_lemma": "direct",
  "word_detail.matched_by_tags": "după temă",

  /* ─── Inflection card (all POS) ─────────────── */
  "inflection.indefinite": "nearticulat",
  "inflection.articulated": "articulat",
  "inflection.singular": "singular",
  "inflection.plural": "plural",
  "inflection.masculine": "masculin",
  "inflection.feminine": "feminin",
  "inflection.m_short": "m.",
  "inflection.f_short": "f.",
  "inflection.n_short": "n.",
  "inflection.verb_prezent_1sg": "prez. pers. 1 sg.",
  "inflection.verb_prezent_3sg": "prez. pers. 3 sg.",
  "inflection.verb_participiu": "participiu",
  "inflection.verb_imperativ_2sg": "imp. pers. 2 sg.",
  "inflection.verb_imperfect_3sg": "imperf. pers. 3 sg.",
  "inflection.verb_perfect_3sg": "perf. pers. 3 sg.",
  "inflection.pron_nominativ": "nominativ",
  "inflection.pron_acc_stressed": "acuz. (acc.)",
  "inflection.pron_acc_unstressed": "acuz. (neacc.)",
  "inflection.pron_dat_stressed": "dat. (acc.)",
  "inflection.pron_dat_unstressed": "dat. (neacc.)",
  "inflection.conjugation_label": "Conjugare",
  "inflection.conjugation_value": "conj. {n}",
  "inflection.reflexive_badge": "reflexiv",
  "inflection.impersonal_badge": "impersonal",
  "inflection.agreement_four_forms": "4 forme",
  "inflection.agreement_two_forms": "2 forme",
  "inflection.agreement_invariable": "invariabil",
  "inflection.gender_f": "f.",
  "inflection.gender_m": "m.",
  "inflection.gender_n": "n.",

  /* ─── Grammar detail — Applies-to section ──── */
  "grammar_detail.applies_to": "Se aplică la",
  "grammar_detail.applies_none": "Nu sunt cuvinte legate.",
  "grammar_detail.applies_count": "{n}",
  "grammar_detail.applies_more": "+{n} altele",
  "grammar_detail.related_topics": "Subiecte conexe",

  /* ─── Grammar detail ─────────────────────── */
  "grammar_detail.not_found": "Regula nu a fost găsită",
  "grammar_detail.back_aria": "Înapoi la gramatică",
  "grammar_detail.quiz_yourself_title": "Testează-te",
  "grammar_detail.quiz_yourself_subtitle": "{n} cartonașe pentru această regulă",
  "grammar_detail.practice": "Practică",
  "grammar_detail.mark_known_new": "Știu această temă",
  "grammar_detail.mark_known_learning": "Știu — marchează ca învățată",
  "grammar_detail.mark_known_confirm":
    'Marcați „{title}" ca învățată? Tema va reapărea peste ~4 luni pentru verificare.',

  /* ─── Practice session (Phase 5a) ──────────────────── */
  "practice.mode_mcq": "MCQ",
  "practice.mode_type": "Scriere",
  "practice.load_error": "Nu s-au putut încărca exercițiile.",
  "practice.empty_title": "Niciun exercițiu încă",
  "practice.empty_body": "Pentru regula „{rule}” nu există încă exerciții practice.",
  "practice.done_title": "Gata!",
  "practice.done_body": "Ai parcurs toate cele {n} cartonașe ale regulii „{rule}”.",
  "practice.do_again": "Reia",

  /* ─── Level-up exam (Phase 7) ─────────────── */
  "exam.lobby_eyebrow": "Examen pentru {level}",
  "exam.lobby_title": "Examenul final",
  "exam.lobby_hero_title": "Pregătire pentru {level}",
  "exam.lobby_hero_sub": "{count} întrebări · prag de promovare {pass}%",
  "exam.section_vocab_mcq": "Vocabular — alegere",
  "exam.section_vocab_type": "Vocabular — scriere",
  "exam.section_grammar_mcq": "Gramatică — alegere",
  "exam.section_grammar_fill": "Gramatică — completare",
  "exam.section_grammar_transform": "Gramatică — transformare",
  "exam.rule_pass_mark": "Trebuie să obții ≥{pass}% pentru a promova.",
  "exam.rule_cooldown": "În caz de eșec, poți reîncerca după {hours} h.",
  "exam.rule_no_srs": "Examenul nu afectează programul de repetiții.",
  "exam.start_button": "Începe examenul · {level}",
  "exam.starting": "Pornim…",
  "exam.submit": "Finalizează",
  "exam.submitting": "Se verifică…",
  "exam.next": "Următoarea",
  "exam.prev": "Înapoi",
  "exam.flag": "Marchează pentru revizuire",
  "exam.unflag": "Anulează marcajul",
  "exam.session_title": "Examen",
  "exam.type_placeholder": "Răspunsul tău",
  "exam.abandon_confirm": "Renunți la examen? Progresul se va pierde.",
  "exam.error_not_ready": "Atinge mai întâi 85% la vocabular și gramatică.",
  "exam.error_cooldown": "Examenul poate fi reluat în aproximativ {hours} h.",
  "exam.error_generic": "Examenul nu a putut fi pornit. Încearcă mai târziu.",
  "exam.result_pass_title": "Felicitări — ai trecut la {level}!",
  "exam.result_pass_sub": "Nivelul {level} este deblocat. Continuă învățarea.",
  "exam.result_fail_title": "Mai trebuie puțin",
  "exam.result_fail_sub": "Ai nevoie de ≥{pass}%. Repetă subiectele slabe și încearcă din nou.",
  "exam.result_breakdown_title": "Pe secțiuni",
  "exam.result_cta_continue": "Continuă · {level}",
  "exam.result_cta_retry_later": "Înapoi la nivel",
  "exam.result_cta_home": "Acasă",
  "exam.weak_area_title": "De exersat",
  "exam.weak_area_body": 'La secțiunea „{section}" — {pct}%. Mai exersează puțin.',
  "exam.weak_area_cta_vocab": "Spre cuvinte",
  "exam.weak_area_cta_grammar": "Spre gramatică",
  "exam.result_cta_review": "Vezi răspunsurile",
  "exam.review_title": "Recapitulare",
  "exam.review_subtitle": "Rezultat: {pct}%",
  "exam.review_your_answer": "Răspunsul tău",
  "exam.review_correct_answer": "Corect",
  "exam.review_blank": "(gol)",
  "exam.review_error": "Recapitularea nu a putut fi încărcată.",
  "settings.reset_level_title": "Resetează nivelul (debug)",
  "settings.reset_level_hint": "Setează currentLevel și șterge tentativele de examen.",
  "settings.reset_level_confirm": "Resetezi la {level}? Toate tentativele vor fi șterse.",
  "settings.reset_level_done": "Nivel: {level} · tentative șterse: {n}",

  /* ─── Grammar page ───────────────────────── */
  /* ─── Level names (CEFR) ─────────────────── */
  "level.name_A0": "Începător absolut",
  "level.name_A1": "Începător",
  "level.name_A2": "Elementar",
  "level.name_B1": "Intermediar",
  "level.name_B2": "Intermediar superior",
  "level.name_C1": "Avansat",
  "level.name_C2": "Stăpânire",

  /* ─── Levels page (hub + focus) ──────────── */
  "level_hub.title": "Parcursul tău",
  "level_hub.currently_learning": "În curs de învățare",
  "level_hub.your_path": "Parcursul tău",
  "level_hub.totals": "{words} cuvinte · {rules} reguli de gramatică",
  "level_hub.level_up_at_both": "85% la ambele pentru avansare",
  "level_hub.cards_to_go": "~{n} cartonașe până la următorul nivel",
  "level_hub.ready": "Gata pentru nivelul următor",
  "level_hub.continue": "Continuă {level}",
  "level_hub.ring_vocab": "Vocabular",
  "level_hub.ring_grammar": "Gramatică",
  "level_hub.bar_vocab": "VOCABULAR",
  "level_hub.bar_grammar": "GRAMATICĂ",
  "level_hub.pill_complete": "finalizat",
  "level_hub.pill_active": "activ",
  "level_hub.n_cards": "{n} cartonașe",
  "level_hub.no_content": "gol încă",
  "level_focus.title_with_lang": "{level} · Română",
  "level_focus.vocab": "Vocabular",
  "level_focus.grammar": "Gramatică",
  "level_focus.threshold_copy": "Ajungi la 85% la ambele pentru a fi gata de {next}",
  "level_focus.threshold_copy_last": "Nivel final. Menține 85% pentru a păstra stăpânirea.",
  "level_focus.ready_copy": "Ești gata! Continuă să exersezi — avansarea la {next} vine în curând.",
  "level_focus.study_cards": "Învață {level}",
  "level_focus.browse_grammar": "Gramatică {level}",
  "level_focus.not_current_hint": "Ești pe {current} acum. Finalizează-l pentru a avansa.",

  /* ─── Grammar page ───────────────────────── */
  "grammar.title": "Gramatică",
  "grammar.totals": "{total} reguli · {known} știute · {learning} se învață",
  "grammar.search_placeholder": "Caută reguli…",
  "grammar.up_next": "Urmează",
  "grammar.read": "Deschide",
  "grammar.practice": "Antrenează",
  "grammar.all_known_eyebrow": "Gramatică {level} completă",
  "grammar.all_known_title": "Ai stăpânit toată gramatica de {level}!",
  "grammar.all_known_body":
    "Toate regulile sunt învățate. Concentrează-te pe vocabular sau aruncă o privire pe pagina de niveluri.",
  "grammar.go_to_levels": "Spre niveluri",
  "grammar.empty_no_match": 'Nicio regulă nu se potrivește cu „{q}".',
  "grammar.empty_filter": "Încă nicio regulă în această categorie.",
  "grammar.empty_all": "Încă nicio regulă. Importați un JSON pentru a începe.",
  "grammar.filter_by_tag": "Filtru după etichetă:",
  "grammar.clear_filter": "Șterge",

  /* ─── Progress page ──────────────────────── */
  "progress.title": "Progres",
  "progress.subtitle_all_time": "Total",
  "progress.stat_words_known": "Cuvinte știute",
  "progress.stat_learning": "Se învață",
  "progress.stat_day_streak": "Zile consecutive",
  "progress.stat_retention": "Retenție",
  "progress.delta_this_week": "+{n} săptămâna asta",
  "progress.delta_avg_ease": "EF mediu {n}",
  "progress.delta_best_streak": "record {n}",
  "progress.delta_last_30d": "ultimele 30 de zile",
  "progress.delta_none": "—",
  "progress.vocabulary": "Vocabular",
  "progress.total_words": "{n} în total",
  "progress.legend_known": "Știute",
  "progress.legend_learning": "Se învață",
  "progress.legend_new": "Noi",
  "progress.activity": "Activitate",
  "progress.last_13_weeks": "Ultimele 13 săptămâni",
  "progress.heatmap_less": "mai puțin",
  "progress.heatmap_more": "mai mult",
  "progress.section_by_level": "Pe niveluri",
  "progress.view_level": "Vezi mai mult",

  /* ─── Study flow ─────────────────────────── */
  "study.loading": "Se încarcă…",
  "study.reveal": "Arată",
  "study.check": "Verifică",
  "study.type_prompt": "Răspunsul dvs.",
  "study.type_placeholder": "Scrieți traducerea…",
  "study.type_correct": "Corect",
  "study.type_empty": "(gol)",
  "study.tap_or_space": "Atingeți cartonașul sau apăsați",
  "study.play_audio": "Ascultă pronunția",
  "study.mcq_pick": "alegeți una",
  "study.grade_again": "Repetă",
  "study.grade_hard": "Greu",
  "study.grade_good": "Bine",
  "study.grade_easy": "Ușor",
  "study.know_action": "Știu deja acest cuvânt",
  "study.know_action_grammar": "Cunosc deja acest subiect",
  "study.know_armed_prompt": "Ești sigur?",
  "study.know_saving": "Se salvează…",
  "study.direction_ro_to_ru": "RO → RU",
  "study.direction_ru_to_ro": "RU → RO",
  "study.badge_type": "scrie",
  "study.badge_grammar": "Gramatică",
  "study.badge_new": "Nou",
  "study.quiz_choose_eyebrow": "Alege forma corectă",
  "study.quiz_type_eyebrow": "Scrie forma corectă",
  "study.quiz_input_placeholder": "Scrie răspunsul…",
  "study.quiz_check": "Verifică",
  "study.quiz_skip": "Sari peste",
  "study.quiz_correct_label": "Corect",
  "study.quiz_correct_answer": "Răspuns corect: {answer}",
  "study.quiz_continue": "Continuă",
  "study.quiz_unsupported_kind": "Acest tip de exercițiu vine în curând.",
  "study.quiz_transform_eyebrow": "Transformă propoziția",
  "study.quiz_transform_source": "Sursă",
  "study.quiz_transform_target": "Rezultat",
  "study.synonyms_short": "sin:",
  "study.all_caught_up_title": "Totul e la zi",
  "study.all_caught_up_body":
    "Acum nu sunt cartonașe de repetat. Reveniți când apar altele noi sau adăugați mai multe cuvinte.",
  "study.refresh": "Reîmprospătează",
  "study.back_to_home": "Înapoi acasă",

  /* ─── Login ──────────────────────────────── */
  "login.title": "Autentificare",
  "login.welcome_back": "Bine ați revenit",
  "login.subtitle": "Intrați pentru a vă continua seria.",
  "login.email": "Email",
  "login.password": "Parolă",
  "login.submit": "Intră",
  "login.submitting": "Se autentifică…",
  "login.error_invalid": "Email sau parolă incorecte",
  "login.error_generic": "Autentificare eșuată",
  "login.need_account": "Aveți nevoie de cont?",
  "login.contact_admin": "Contactați administratorul",
  "login.sign_up": "Înregistrați-vă",
  "login.forgot_password": "Ați uitat parola?",

  /* ─── Forgot password ────────────────────── */
  "forgot.title": "Resetare parolă",
  "forgot.subtitle": "Introduceți adresa — vă trimitem un link de resetare.",
  "forgot.email": "Email",
  "forgot.submit": "Trimite linkul",
  "forgot.submitting": "Se trimite…",
  "forgot.back_to_login": "Înapoi la autentificare",
  "forgot.sent_title": "Verificați-vă inboxul",
  "forgot.sent_body":
    "Dacă există un cont pentru {email}, am trimis un link de resetare. Linkul este valabil 60 de minute.",
  "forgot.sent_resend": "Nu a sosit? Trimiteți din nou",

  /* ─── Reset password ─────────────────────── */
  "reset.title": "Parolă nouă",
  "reset.subtitle": "Alegeți o parolă sigură — minim 8 caractere.",
  "reset.password": "Parolă nouă",
  "reset.password_placeholder": "minim 8 caractere",
  "reset.confirm_password": "Confirmați parola",
  "reset.submit": "Salvează parola",
  "reset.submitting": "Se salvează…",
  "reset.success_toast": "Parola a fost actualizată — autentificați-vă din nou.",
  "reset.error_invalid_link": "Link invalid sau expirat",
  "reset.invalid_link_title": "Linkul nu funcționează",
  "reset.invalid_link_body": "Linkul s-ar putea să fi expirat sau să fi fost deja folosit. Solicitați unul nou.",
  "reset.request_new_link": "Solicită un link nou",

  /* ─── Verify email ───────────────────────── */
  "verify.pending_title": "Confirmăm emailul…",
  "verify.pending_body": "Un moment — verificăm linkul din email.",
  "verify.success_title": "Email confirmat",
  "verify.success_body": "Mulțumim! Contul este activ. Puteți reveni la învățare.",
  "verify.success_cta": "La pagina principală",
  "verify.error_title": "Linkul nu funcționează",
  "verify.error_body":
    "Linkul pare să fi expirat sau să fi fost deja folosit. Autentificați-vă și solicitați unul nou.",
  "verify.error_cta": "Autentificare și solicitare nouă",
  "verify.resend": "Trimite din nou",
  "verify.resend_sent": "Emailul a fost trimis — verificați-vă inboxul.",
  "verify.resend_error": "Nu s-a putut trimite — încercați mai târziu.",

  /* ─── Validation ─────────────────────────── */
  "validation.required": "Câmp obligatoriu",
  "validation.email_invalid": "Format email invalid",
  "validation.password_too_short": "Minim 8 caractere",
  "validation.password_too_long": "Parola e prea lungă",
  "validation.passwords_must_match": "Parolele nu coincid",
  "validation.token_invalid": "Link invalid",

  /* ─── Auth error codes ───────────────────── */
  "auth.errors.invalid_credentials": "Email sau parolă incorecte",
  "auth.errors.email_already_registered": "Acest email este deja înregistrat",
  "auth.errors.reset_link_invalid": "Linkul de resetare este invalid sau a expirat",
  "auth.errors.verify_link_invalid": "Linkul de confirmare este invalid sau a expirat",
  "auth.errors.rate_limited": "Prea multe încercări. Încercați peste {seconds} s.",
  "auth.errors.unauthorized": "Autentificare necesară",
  "auth.errors.unknown": "Ceva a mers prost. Încercați mai târziu.",

  /* ─── Sign up ────────────────────────────── */
  "signup.title": "Creați un cont",
  "signup.subtitle": "Înregistrați-vă — seria de lecții începe acum.",
  "signup.name": "Nume",
  "signup.name_placeholder": "Cum să vă spunem",
  "signup.email": "Email",
  "signup.password": "Parolă",
  "signup.password_placeholder": "minim 8 caractere",
  "signup.confirm_password": "Confirmați parola",
  "signup.submit": "Înregistrare",
  "signup.submitting": "Se creează…",
  "signup.have_account": "Aveți deja un cont?",
  "signup.sign_in": "Autentificare",

  /* ─── Settings ───────────────────────────── */
  "settings.title": "Setări",
  "settings.section_admin": "Administrare",
  "settings.admin_link_title": "Panou administrator",
  "settings.admin_link_sub": "Importă cuvinte și reguli de gramatică",
  "settings.section_account": "Cont",
  "settings.name_label": "Nume",
  "settings.name_placeholder": "Nesetat",
  "settings.signed_in_as": "Autentificat ca",
  "settings.section_appearance": "Aspect",
  "settings.theme_light": "Luminos",
  "settings.theme_dark": "Întunecat",
  "settings.theme_system": "Sistem",
  "settings.section_language": "Limba interfeței",
  "settings.language_ru": "Русский",
  "settings.language_ro": "Română",
  "settings.section_study_mode": "Mod de studiu",
  "settings.study_mode_title": "Deschis sau ghidat",
  "settings.study_mode_hint": "Deschis — toate nivelurile amestecate. Nivel — cale pas cu pas. Progresul e comun.",
  "settings.mode_open_label": "Deschis",
  "settings.mode_open_sub": "Toate nivelurile",
  "settings.mode_level_label": "Nivel",
  "settings.mode_level_sub": "Acum {level}",
  "settings.section_current_level": "Nivel curent",
  "settings.current_level_title": "Unde sunteți acum",
  "settings.current_level_hint":
    "Puteți schimba manual — de exemplu, pentru a revedea un nivel anterior sau a sări peste unul deja parcurs.",
  "settings.level_confirm": "Treceți de la {current} la {next}? Progresul nu se pierde — puteți reveni oricând.",
  "settings.section_study": "Studiu",
  "settings.daily_batch_title": "Cuvinte noi pe zi",
  "settings.daily_batch_hint": "Câte cuvinte noi să introducem zilnic.",
  "settings.new_grammar_title": "Reguli noi pe zi",
  "settings.new_grammar_hint": "Gramatica e formată din concepte, nu cartonașe. Mai bine treptat. 0 = pauză.",
  "settings.new_quiz_title": "Exerciții noi pe zi",
  "settings.new_quiz_hint": "Antrenamente pentru reguli pe care le-ai văzut deja. 0 = pauză.",
  "settings.section_reminders": "Memento-uri",
  "settings.push_title": "Notificări push",
  "settings.push_status_enabled_here": "Activate pe acest dispozitiv",
  "settings.push_status_plus_others": " · încă {n}",
  "settings.push_status_only_others": "Activate pe {n} alt dispozitiv",
  "settings.push_status_only_others_plural": "Activate pe {n} alte dispozitive",
  "settings.push_status_not_enabled": "Încă nu sunt activate.",
  "settings.push_status_https": "Necesită HTTPS (sau localhost pentru dezvoltare).",
  "settings.push_status_unsupported": "Browserul dvs. nu suportă push.",
  "settings.push_status_unconfigured": "Cheile de pe server nu sunt încă configurate.",
  "settings.push_enable": "Activează memento-uri",
  "settings.push_enabling": "Se activează…",
  "settings.push_disable": "Dezactivează pe acest dispozitiv",
  "settings.push_disabling": "Se dezactivează…",
  "settings.push_send_test": "Trimite un test",
  "settings.push_sending_test": "Se trimite…",
  "settings.push_test_result": "Livrate: {delivered} din {attempted}",
  "settings.push_test_none_attempted": "Nu există abonamente — activați memento-urile mai întâi",
  "settings.push_test_none_delivered": "0 din {attempted} livrate — verificați logurile serverului",
  "settings.push_requires_https": "Push necesită un context securizat (HTTPS sau localhost).",
  "settings.push_unavailable": "Push nu este disponibil în acest browser.",
  "settings.push_server_unconfigured": "Serverul nu e configurat încă — rulați",
  "settings.section_install": "Instalare aplicație",
  "settings.sign_out": "Ieșire",
  "settings.save_failed": "Salvarea a eșuat",

  /* ─── PWA update prompt ──────────────────── */
  "pwa.update_available": "Actualizare disponibilă",
  "pwa.update_hint": "Reîmprospătați pentru a primi cea mai nouă versiune.",
  "pwa.reload": "Reîmprospătează",
  "pwa.dismiss": "Închide",

  /* ─── PWA install prompt ─────────────────── */
  "pwa.install_title": "Instalează rmnlng",
  "pwa.install_hint": "Acces rapid direct de pe ecranul principal.",
  "pwa.install_action": "Instalează",
  "pwa.install_ios_title": "Adaugă pe ecranul principal",
  "pwa.install_ios_hint_before": "Apăsați ",
  "pwa.install_ios_hint_after": ' apoi „Adaugă pe ecranul principal".',

  /* ─── Skills mode (scenarios) ─────────────── */
  "nav.skills": "Abilități",
  "settings.mode_scenarios_label": "Scenarii",
  "settings.mode_scenarios_sub": "Abilități de comunicare",
  "skills.title": "Jurnal de abilități",
  "skills.hero_label": "Pot",
  "skills.hero_count_of": "din {total} abilități",
  "skills.continue": "Continuă",
  "skills.submit_checkpoint": "Susține testul",
  "skills.start_scenario": "Începe scenariul",
  "skills.start_anyway": "Începe oricum",
  "skills.section_in_progress": "În progres",
  "skills.section_ready_check": "Gata pentru test",
  "skills.section_available": "Gata de început",
  "skills.section_soft_locked": "Posibil, dar dificil",
  "skills.section_locked": "Blocat",
  "skills.section_completed": "Stăpânit",
  "skills.detail_promise_label": "După aceasta vei putea",
  "skills.detail_words": "Cuvinte",
  "skills.detail_rules": "Reguli",
  "skills.detail_constructions": "Construcții",
  "skills.detail_words_known": "{known} / {total} cunoscute",
  "skills.detail_rules_open": "{open} / {total} deschise",
  "skills.detail_constructions_count": "{count} fraze gata",
  "skills.detail_words_need": "Mai sunt {n}",
  "skills.detail_words_ready": "✓ gata",
  "skills.detail_checkpoint_hint": "{count} sarcini care combină aceste cuvinte și reguli.",
  "skills.detail_progress_minutes": "~{n} min",
  "skills.detail_capstone": "Capstone secțiune",
  "skills.locked_why_title": "Se va deschide după",
  "skills.locked_explain_title": "Blocat",
  "skills.locked_status_done": "Stăpânit",
  "skills.locked_status_ready_check": "Gata pentru test",
  "skills.locked_status_in_progress": "În progres",
  "skills.locked_status_available": "Gata de început",
  "skills.locked_status_soft_locked": "Posibil, dar dificil",
  "skills.locked_status_locked": "Blocat",
  "skills.locked_back": "Am înțeles",
  "skills.empty_no_scenarios": "Scenariile vor apărea în curând",
  "skills.empty_no_started": "Începe cu orice scenariu de mai jos — primul tău pas.",
  "skills.soft_lock_warning_title": "Va fi dificil",
  "skills.soft_lock_warning_hint":
    "Cunoști puține cuvinte. Poți începe oricum — dar recomandăm să studiezi mai întâi vocabularul.",
  "skills.scenario_meta": "{words} cuvinte · {rules} reguli · ~{mins} min",
  "skills.locked_meta_after": "după: {names}",
  "skills.continue_in_progress": "În progres",
  "skills.continue_ready_check": "✓ gata pentru test",
  "skills.session_topbar_label": "scenariu",
  "skills.session_step_word": "Cuvinte · {idx} / {total}",
  "skills.session_step_rule": "Regulă · {idx} / {total}",
  "skills.session_step_line": "Frază · {idx} / {total}",
  "skills.session_step_mini_check": "Mini-check · {idx} / {total}",
  "skills.session_continue": "Mai departe",
  "skills.session_next": "Următor",
  "skills.session_understood": "Am înțeles — mai departe",
  "skills.session_to_practice": "La verificare →",
  "skills.session_practice_label": "Verificare",
  "skills.session_open_full": "Deschide explicația completă",
  "skills.session_check": "Verifică",
  "skills.session_skip": "Sari peste",
  "skills.session_translate_to_ro": "Tradu în română",
  "skills.session_input_placeholder": "scrie fraza",
  "skills.session_correct": "Corect",
  "skills.session_wrong": "Nu chiar — corect: {answer}",
  "skills.session_rule_summary": "Pe scurt",
  "skills.session_rule_proof": "Să verificăm",
  "skills.session_quick_check": "Verificare rapidă",
  "skills.session_word_known_skip": "Deja le știi — sărim peste",
  "skills.material_title": "Materialul colectat",
  "skills.material_subtitle": "Cuvintele, regulile și frazele pentru „{title}” le ai în cap.",
  "skills.material_words_collected": "cuvinte",
  "skills.material_rules_collected": "reguli",
  "skills.material_lines_collected": "fraze",
  "skills.material_take_checkpoint": "Susține testul · ~{mins} min",
  "skills.material_later": "Mai târziu",
  "skills.checkpoint_label": "test",
  "skills.checkpoint_progress": "{idx} / {total}",
  "skills.checkpoint_loading": "Pregătim întrebările…",
  "skills.checkpoint_empty": "Nu există încă un test pentru acest scenariu.",
  "skills.checkpoint_exit_confirm": "Ieși? Progresul se va pierde.",
  "skills.checkpoint_submit": "Trimite răspunsurile",
  "skills.checkpoint_select_answer": "Alege o variantă",
  "skills.checkpoint_type_answer": "Scrie răspunsul",
  "skills.checkpoint_translation_label": "Traducere",
  "skills.checkpoint_source_label": "Din",
  "skills.result_passed_title": "Felicitări!",
  "skills.result_passed_score": "{correct} din {total} · {pct}%",
  "skills.result_passed_subtitle": "Scenariu finalizat. Acum poți:",
  "skills.result_passed_cta_next": "Ce urmează",
  "skills.result_passed_cta_journal": "La jurnal",
  "skills.result_borderline_title": "Se poate accepta",
  "skills.result_borderline_subtitle":
    "{correct} din {total}. Decide: întărești punctul slab acum sau închizi și revii mai târziu.",
  "skills.result_borderline_weak": "Puncte slabe",
  "skills.result_borderline_accept": "Acceptă ca finalizat",
  "skills.result_borderline_repeat": "Repetă această temă",
  "skills.result_failed_title": "Aproape — {correct} / {total}",
  "skills.result_failed_subtitle": "Încă o sesiune și o închidem.",
  "skills.result_failed_weak": "Puncte slabe",
  "skills.result_failed_cta_resume": "Reia punctele slabe",
  "skills.result_failed_cta_break": "Pauză · revin mai târziu",
  "skills.result_review_correct": "Corect",
  "skills.result_review_wrong": "Greșit",
  "skills.result_review_expected": "Corect: {answer}",
  "skills.now_you_can_title": "Acum poți",
  "skills.now_you_can_unlocked": "S-a deblocat",
  "skills.now_you_can_continue": "Continuă drumul",
  "skills.now_you_can_journal": "Înapoi la jurnal",
  "skills.tree_title": "Harta",
  "skills.tree_back": "← La jurnal",
  "skills.tree_stage": "Etapa {n}",
  "skills.tree_legend_completed": "Finalizat",
  "skills.tree_legend_in_progress": "În curs",
  "skills.tree_legend_available": "Disponibil",
  "skills.tree_legend_locked": "Blocat",
  "skills.tree_journal_link": "Harta drumului →",
  "skills.stall_title": "Pauză în skills",
  "skills.stall_subtitle":
    "Toate scenariile disponibile sunt finalizate. Învață mai multe cuvinte — și vor apărea altele.",
  "skills.stall_domain_have": "{have} / {need} cuvinte cunoscute",
  "skills.stall_domain_unlocks": "deblochează {n} {label}",
  "skills.stall_domain_unlocks_one": "scenariu",
  "skills.stall_domain_unlocks_few": "scenarii",
  "skills.stall_domain_unlocks_many": "scenarii",
  "skills.stall_domain_cta": "Învață",
  "skills.stall_switch_mode": "sau treci în modul deschis",
  "skills.section_needs_review": "De reîmprospătat",
  "skills.needs_review_meta": "A trecut ceva timp, repetă",
  "home_skills.next_up": "Disponibile acum",
  "home_skills.journal_link": "Jurnal",
  "home_skills.empty_journal": "Deschide jurnalul →",
  "home_skills.start_here": "Începe aici",
  "home_skills.start_cta": "Începe scenariul",
  "home_skills.mini_map_title": "Drumul tău",
  "home_skills.mini_map_open_full": "Toată harta",
  "home_skills.mode_hint_title": "Skills mode",
  "home_skills.mode_hint_body":
    "Înveți prin scenarii comunicative. Cuvintele și regulile apar ca materiale pentru scopul concret.",
  "mode_switch.label_open": "Open",
  "mode_switch.sub_open": "Free",
  "mode_switch.sub_level": "Level",
  "mode_switch.sub_skills": "Skills",
  "skills.tag_capstone": "capstone",
  "skills.tag_scenario": "scenariu",
  "skills.tag_checkpoint": "Test",
  "skills.tag_journal_back": "← Jurnal",
  "skills.tag_pause": "pauză",
  "skills.tag_soft_locked_inline": "dificil",
  "skills.weak_rule_badge": "rg",
  "skills.weak_rule_errors_one": "{n} greșeală",
  "skills.weak_rule_errors_few": "{n} greșeli",
  "skills.weak_rule_errors_many": "{n} de greșeli",
  "skills.step_glyph_word": "C",
  "skills.step_glyph_rule": "R",
  "skills.step_glyph_chunk": "F",
  "skills.rule_popup_close": "Închide și continuă",
};
